import React from 'react';
import { useForm, UseFormProps } from 'react-hook-form';

import { useBreakpoint } from '@ast/magma/breakpoints';

import { Dialog, DialogButtonType, DialogProps } from '@ast/magma/components/dialog';

import { useTranslation } from '@app/core/i18n';

import { Portal } from '../Portal';

import { FormProviderWrapper } from '../FormProviderWrapper/FormProviderWrapper';

export interface PortalDialogFormProps {
  /**
   * react-hook form options
   */
  formOptions: UseFormProps
  dialogOptions?: DialogProps,
  onSubmit: (data: any) => void;
}

/**
 * Used to show form and its fields inside the Dialog component via DOM-root Portal
 */
export const PortalDialogForm:React.FC<PortalDialogFormProps> = ({
  children,
  formOptions,
  dialogOptions,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const isMobile = useBreakpoint().device === 'mobile';
  const formMethods = useForm(formOptions);

  const submitIfValid = formMethods.handleSubmit(() => {
    onSubmit(formMethods.getValues());
  });

  return (
    <Portal>
      <Dialog
        predefinedWidth="md"
        align={isMobile ? 'bottom' : 'center'}
        fixedTitle={false}
        buttons={[
          {
            type: DialogButtonType.Cancel,
            label: t('portal-dialog.cancel-button|Portal dialog cancel button', 'Cancel'),
          },
          {
            type: DialogButtonType.OK,
            label: t('portal-dialog.save-button|Portal dialog save button label', 'Save'),
            onClick: () => {
              submitIfValid();
              return true;
            },
          },
        ]}
        defaultButton={DialogButtonType.OK}
        data-stable-name="WizardDialogForm"
        {...dialogOptions}
      >
        <FormProviderWrapper
          formProviderProps={formMethods}
          formProps={{
            onSubmit: (e) => {
              // prevent other forms submitting if it's a modal form
              e.preventDefault();
              e.stopPropagation();
              submitIfValid();
            },
          }}
        >
          {children}
        </FormProviderWrapper>
      </Dialog>
    </Portal>
  );
};
